import {
  Combobox,
  Listbox,
  Popover,
  TextField
} from '@superops/ui-design-system';
import {
  DATE_FORMAT,
  DATE_TIME_FORMAT,
  TIME_FORMAT_HH_MM_A
} from '@nile/components/src/Constants';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AppContext, getTranslatedText } from '@nile/components';
import { isEmpty } from 'lodash';
import { useIntl } from 'react-intl';
const isToday = require('dayjs/plugin/isToday');

dayjs.extend(isToday);

interface Option {
  label: string;
  value: string;
}

interface TimeSelectionDropdownProps {
  value: string;
  minTime: string;
  maxTime: string;
  date: string;
  onChange: (value: string) => void;
  placeholderText: string;
  timeFormat: string;
  disabled: boolean;
  showBorder?: boolean;
}

type Options = Option[];

type TimeUnitType = 'minutes' | 'hours';

function TimeSelectionDropdown(
  props: TimeSelectionDropdownProps
): React.ReactElement {
  const appContext = useContext(AppContext);
  const {
    timeFormat: timeFormatFromContext
  }: { timeFormat: string } = appContext?.companyInfo;

  const {
    date,
    value,
    minTime,
    maxTime,
    onChange,
    placeholderText,
    disabled = false,
    timeFormat: timeFormatFromProps,
    showBorder = false
  } = props;

  const intl = useIntl();
  const comboboxRef = useRef();

  const timeFormat =
    timeFormatFromProps || timeFormatFromContext || TIME_FORMAT_HH_MM_A;

  const twelveHrFormat = /hh+/g.test(timeFormat);

  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [timeInput, setTimeInput] = useState<Option | null>(
    value
      ? {
          label: `${dayjs(value).format(timeFormat)}`,
          value: dayjs(value).format(DATE_TIME_FORMAT)
        }
      : null
  );

  const options = useMemo(() => {
    let Options = [];
    /** If minTime is given dropdown options will start from the given mintime */
    let newDate = minTime
      ? dayjs(minTime)
      : date
      ? dayjs(date).startOf('day')
      : dayjs(new Date()).format(DATE_FORMAT);

    let nextDate = newDate;

    while (dayjs(newDate).isSame(nextDate, 'day')) {
      let difference;
      if (maxTime) {
        difference = dayjs(maxTime).diff(nextDate, 'minutes');
      } else {
        difference = dayjs(nextDate).diff(newDate, 'minutes');
      }
      if ((!minTime && !maxTime) || difference > 0) {
        Options.push({
          label: `${dayjs(nextDate).format(timeFormat)}`,
          value: dayjs(nextDate).format(DATE_TIME_FORMAT)
        });
      }
      nextDate = dayjs(nextDate).add(15, 'minutes');
    }
    // setOptions(Options);

    return Options;
  }, [date, minTime, maxTime]);

  function getValidTime(input: string): string {
    if (/[+-]?([0-9]*[:])?[0-9]+/g.test(input)) {
      // const inputAm = input.match(/[+-]?([0-9]*[:])?[0-9]+ *(am*)+/gi);
      const inputPm = input.match(/[+-]?([0-9]*[:])?[0-9]+ *(pm*)+/gi);

      let timeInHrs = parseInt(input.split(':')[0]?.trim() || '0');
      const timeInMins = parseInt(input.split(':')[1]?.trim() || '0');

      if (twelveHrFormat) {
        if (timeInHrs > 0 && timeInHrs < 12 && inputPm) {
          timeInHrs += 12;
        }
        if (timeInHrs === 12 && !inputPm) {
          timeInHrs -= 12;
        }
      }

      const totalTimeInMins = timeInHrs * 60 + timeInMins;

      const currentDate = date || new Date();

      const resultTime = dayjs(currentDate)
        .startOf('day')
        .startOf('hour')
        .add(totalTimeInMins, 'minutes')
        .format(DATE_TIME_FORMAT);

      return resultTime;
    }
  }

  function timeInputHandler(value: string) {
    const sanitizedValue = value.replace(/(?<=:):/gi, '');
    if (sanitizedValue) {
      const isDeleteing =
        timeInput?.label && timeInput.label.length > sanitizedValue.length;
      setTimeInput({
        label:
          !isDeleteing &&
          sanitizedValue.length === 2 &&
          !/:/gi.test(sanitizedValue)
            ? `${sanitizedValue}:`
            : sanitizedValue,
        value: getValidTime(sanitizedValue)
      });
    } else {
      setTimeInput(null);
    }
  }

  function timeSelectHandler(selectedOption: Option) {
    setTimeInput(selectedOption);
    if (onChange) onChange(selectedOption?.value);
  }

  function handleSelect(value: string) {
    const selectedOption = filteredOptions.find((opt) => opt?.value === value);
    if (selectedOption) {
      timeSelectHandler({
        ...selectedOption,
        label: dayjs(selectedOption?.value).format(timeFormat)
      });
    }
    setDropdownOpen(false);
  }

  function handleTimeFromProps() {
    setTimeInput({
      label: `${dayjs(value).format(timeFormat)}`,
      value: dayjs(value).format(DATE_TIME_FORMAT)
    });
  }

  const validTime = (option) => {
    if (maxTime && minTime) {
      return (
        dayjs(option?.value).diff(minTime, 'minutes') > 0 &&
        dayjs(maxTime).diff(option?.value, 'minutes') > 0 &&
        dayjs(option?.value).isSame(maxTime, 'day') &&
        dayjs(option?.value).isSame(minTime, 'day')
      );
    }
    if (maxTime) {
      return (
        dayjs(maxTime).diff(option?.value, 'minutes') > 0 &&
        dayjs(option?.value).isSame(maxTime, 'day')
      );
    }
    if (minTime) {
      return (
        dayjs(option?.value).diff(minTime, 'minutes') > 0 &&
        dayjs(option?.value).isSame(minTime, 'day')
      );
    }
    return dayjs(option?.value).isSame(date, 'day');
  };

  const filteredOptions = useMemo(() => {
    let resultArray = options;
    let exactMatches = [];
    let relativeMatches = [];

    if (maxTime || minTime) {
      resultArray = resultArray.filter(validTime);
    }

    if (timeInput?.value) {
      const timeInHrs = timeInput.label.split(':')[0];

      for (let option of resultArray) {
        const timeInHrs = timeInput.label.split(':')[0];
        const timeInMins = timeInput.label.split(':')[1];

        // Find the exact matches based on the input time(minutes)
        if (timeInput?.value && timeInput?.value === option.value) {
          exactMatches.push(option);

          // Find the relative matches based on the input string
        } else if (option?.label.split(':')[0].includes(timeInHrs)) {
          if (
            !timeInMins ||
            (timeInMins && option?.label.split(':')[1].includes(timeInMins))
          ) {
            relativeMatches.push(option);
          }
        }
      }

      if (exactMatches.length || relativeMatches.length) {
        resultArray = [...exactMatches, ...relativeMatches];
      } else {
        let timeInvalid = !validTime(timeInput);

        if (
          twelveHrFormat &&
          parseInt(timeInHrs) > 0 &&
          parseInt(timeInHrs) < 12
        ) {
          const timeInputValuePm = dayjs(timeInput.value).add(12, 'hours');
          timeInvalid =
            !validTime(timeInput) &&
            !validTime({ ...timeInput, value: timeInputValuePm });
        }

        // Return maxTime or minTime if user input is not between allowed time limit
        if (timeInvalid) {
          let timeAllowed = dayjs(date).endOf('day');
          if (minTime && timeInvalid) {
            timeAllowed = dayjs(minTime).add(1, 'minute');
          }
          if (maxTime && timeInvalid) {
            timeAllowed = dayjs(maxTime).subtract(1, 'minute');
          }
          resultArray = [
            {
              value: dayjs(timeAllowed).format(DATE_TIME_FORMAT),
              label: dayjs(timeAllowed).format(timeFormat)
            }
          ];
        }
        // Append a new option to match user input, if no exact matches in available options
        else {
          let time = getValidTime(timeInput?.label);

          if (validTime({ value: time })) {
            exactMatches.push({
              value: time,
              label: dayjs(time).format(timeFormat)
            });
          }
          if (
            twelveHrFormat &&
            !/[+-]?([0-9]*[:])?[0-9]+ *(pm*|am*)+/gi.test(timeInput?.label)
          ) {
            let timePm;
            timePm = dayjs(time)
              .add(12, 'hours')
              .format(DATE_TIME_FORMAT);

            if (validTime({ value: timePm }))
              exactMatches.push({
                value: timePm,
                label: dayjs(timePm).format(timeFormat)
              });
          }
          resultArray = [...exactMatches];
        }
      }
    }

    return resultArray;
  }, [timeInput?.label, minTime, maxTime, options]);

  function translateTimeUnit(timeUnit: TimeUnitType, time: number) {
    switch (timeUnit) {
      case 'minutes':
        return ` ${getTranslatedText(intl, 'timer.duration.unit.minutes', {
          durationMins: time
        })}`;
      case 'hours':
      default:
        return getTranslatedText(intl, 'timer.duration.unit.hours', {
          durationHrs: time
        });
    }
  }

  function openComboboxManually(event: KeyboardEvent) {
    if (
      comboboxRef.current?.openPopover &&
      (event.key === 'Enter' || event.key === 'ArrowDown')
    ) {
      comboboxRef.current?.openPopover();
    }
  }

  useEffect(() => {
    if (value) {
      handleTimeFromProps();
    } else {
      setTimeInput(null);
    }
  }, [value]);

  useEffect(() => {
    document.addEventListener('keydown', openComboboxManually);
    return () => {
      document.removeEventListener('keydown', openComboboxManually);
    };
  }, []);

  return (
    <div className="fw br10">
      <Combobox
        // active={dropdownOpen}
        preferredPosition="below"
        onClose={() => {
          if (
            timeInput?.value &&
            !dayjs(timeInput?.value).isSame(dayjs(value))
          ) {
            timeSelectHandler(filteredOptions[0]);
          }
          setDropdownOpen(false);
        }}
        activator={
          <Combobox.TextField
            type="text"
            value={timeInput?.label || ''}
            placeholder={placeholderText}
            label={placeholderText}
            autoComplete="off"
            onChange={timeInputHandler}
            selectValueOnFocus
            // onFocus={(event) => {
            //   event.target.select();
            //   setDropdownOpen(true);
            // }}
            onBlur={() => {
              if (isEmpty(timeInput?.value) && value) {
                handleTimeFromProps();
              }
            }}
            noborder={!showBorder}
            bgTransparent
            labelHidden
            disabled={disabled}
          />
        }
        zIndexOverride={520}
        fullWidth={false}
      >
        <Listbox onSelect={handleSelect}>
          {filteredOptions?.length ? (
            filteredOptions.map((opt) => {
              const { label, value } = opt;
              let difference, hours, minutes;
              if (maxTime) {
                difference = dayjs(maxTime).diff(value, 'minutes');
              } else if (minTime) {
                difference = dayjs(value).diff(dayjs(minTime), 'minutes');
              }
              if (difference) {
                hours = parseInt(Math.floor(difference / 60).toString());
                minutes = difference % 60;
              }
              return (
                <Listbox.Option
                  key={`${value}`}
                  value={value.toString()}
                  // selected={selectedOption === value}
                  accessibilityLabel={label}
                >
                  {`${label} ${
                    (minTime || maxTime) && (hours || minutes)
                      ? `(${hours ? translateTimeUnit('hours', hours) : ''} ${
                          minutes ? translateTimeUnit('minutes', minutes) : ''
                        })`
                      : ''
                  }`}
                </Listbox.Option>
              );
            })
          ) : maxTime ? (
            <div className="p5 ptr">Minimum duration is 1 min</div>
          ) : (
            <div className="p5 ptr">Duration must be less than 24 hrs</div>
          )}
        </Listbox>
      </Combobox>
    </div>
  );
}

export default TimeSelectionDropdown;
